import { useEffect } from "react";

function ensureMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

function removeMeta(selector) {
  document.head.querySelector(selector)?.remove();
}

function ensureCanonical(canonical) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = canonical;
}

function updateSocialImage(image, imageAlt) {
  if (!image) {
    removeMeta('meta[property="og:image"]');
    removeMeta('meta[property="og:image:alt"]');
    removeMeta('meta[name="twitter:image"]');
    removeMeta('meta[name="twitter:image:alt"]');
    return;
  }
  ensureMeta('meta[property="og:image"]', { property: "og:image", content: image });
  ensureMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });
  ensureMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
  ensureMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt });
}

function updateStructuredData(structuredData) {
  const selector = 'script[data-kavia-structured-data="true"]';
  if (!structuredData) {
    document.head.querySelector(selector)?.remove();
    return;
  }
  let script = document.head.querySelector(selector);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.kaviaStructuredData = "true";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(structuredData);
}

export function useSiteSeo({ title, description, canonical, locale = "es-CO", image = "", imageAlt = "", themeColor = "#070c12", type = "website", structuredData = null }) {
  useEffect(() => {
    document.title = title;
    document.documentElement.lang = locale;
    ensureMeta('meta[name="description"]', { name: "description", content: description });
    ensureMeta('meta[name="theme-color"]', { name: "theme-color", content: themeColor });
    ensureMeta('meta[property="og:type"]', { property: "og:type", content: type });
    ensureMeta('meta[property="og:title"]', { property: "og:title", content: title });
    ensureMeta('meta[property="og:description"]', { property: "og:description", content: description });
    ensureMeta('meta[property="og:locale"]', { property: "og:locale", content: locale.replace("-", "_") });
    ensureMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    ensureCanonical(canonical);
    updateSocialImage(image, imageAlt || title);
    updateStructuredData(structuredData);
    return undefined;
  }, [canonical, description, image, imageAlt, locale, structuredData, themeColor, title, type]);
}

export function useProductSeo(product, market, canonicalOverride, enabled = true) {
  useEffect(() => {
    if (!product || !enabled) return undefined;
    const marketConfig = product.markets[market];
    const title = product.seo.title;
    const description = product.seo.description;
    const canonical = canonicalOverride || product.seo.canonical || window.location.href;
    document.title = title;
    document.documentElement.lang = marketConfig.locale;
    ensureMeta('meta[name="description"]', { name: "description", content: description });
    ensureMeta('meta[property="og:title"]', { property: "og:title", content: title });
    ensureMeta('meta[property="og:description"]', { property: "og:description", content: description });
    ensureMeta('meta[property="og:locale"]', { property: "og:locale", content: marketConfig.locale.replace("-", "_") });
    ensureMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    ensureMeta('meta[name="theme-color"]', { name: "theme-color", content: product.theme?.background || "#f8f0e8" });
    ensureCanonical(canonical);
    updateSocialImage(product.seo.image, product.seo.imageAlt || product.name);
    return undefined;
  }, [canonicalOverride, enabled, market, product]);
}
